import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Modal, Tabs } from 'antd';
import { FJ110, FJ111, j201Api } from '../../services/j201Api';
import { BatchAcceptanceBridge, pwj201f1AcceptanceManifest } from '../../features/acceptance';
import { useModulePermission } from '../../hooks/usePermission';
import { useJ201Mode } from './hooks/useJ201Mode';
import { useJ201Record } from './hooks/useJ201Record';
import { useJ201Navigation } from './hooks/useJ201Navigation';
import { useJ201LookupNames } from './hooks/useJ201LookupNames';
import { useJ201Approval } from './hooks/useJ201Approval';
import { useJ201Calculation } from './hooks/useJ201Calculation';
import J201FormFields from './components/J201FormFields';
import J201DetailTable from './components/J201DetailTable';
import { SharedActionBar, ApprovalState } from '../../components/SharedActionBar';
import ApprovalHistoryModal from './components/ApprovalHistoryModal';
import { getApprovalBadge } from './helpers';
import './J201Form.css';

const MODULE_CODE = 'pwj201f1';

const J201Form: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reviewId = searchParams.get('review');
  const [activeTab, setActiveTab] = useState('1');
  const permissions = useModulePermission(MODULE_CODE);

  // ---- Hooks ----
  const { mode, setMode, isEditing } = useJ201Mode();
  const { names, resolveAllNames, resolveSingleName, clearNames } = useJ201LookupNames();
  const {
    record, formData, details, loading,
    setDetails,
    loadRecord, setField, getField,
    getDetailField, setDetailField,
    handleSave, handleDelete,
    revertToRecord, resetForAdd, applyResult,
    addDetailRow, deleteDetailRow,
  } = useJ201Record();
  const { recalcAll, calcDetailRow } = useJ201Calculation();

  // Wrap loadRecord to also resolve names
  const loadRecordWithNames = useCallback(async (id: string) => {
    const result = await loadRecord(id);
    if (result) {
      resolveAllNames(result.data);
    }
  }, [loadRecord, resolveAllNames]);

  const { allIds, currentIdx, setCurrentIdx, loadList, navTo } = useJ201Navigation(loadRecordWithNames);

  // Approval hook
  const {
    approvalHistoryVisible, setApprovalHistoryVisible,
    handleSubmit, handleApprove, handleReject, handleWriteoff, handleViewHistory,
  } = useJ201Approval({ record, isEditing, loadRecordWithNames });

  // ---- Initial load ----
  useEffect(() => {
    (async () => {
      try {
        const ids = await loadList();
        if (reviewId) {
          const idx = ids.indexOf(reviewId);
          if (idx >= 0) setCurrentIdx(idx);
          await loadRecordWithNames(reviewId);
        } else if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecordWithNames(ids[0]);
        }
      } catch {
        console.error('Failed to load J201 list');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Detail change handler with auto-calc ----
  const handleDetailChange = useCallback((idx: number, key: keyof FJ111, value: FJ111[keyof FJ111]) => {
    setDetails(prev => {
      const next = [...prev];
      if (next[idx]) {
        next[idx] = { ...next[idx], [key]: value };
        // Auto-calc row amounts when qty or price changes
        if (key === 'fj111_13' || key === 'fj111_15' || key === 'fj111_151') {
          next[idx] = calcDetailRow(next[idx]);
        }
      }
      return next;
    });
  }, [setDetails, calcDetailRow]);

  // ---- Recalculate totals before save ----
  const doRecalcAndSave = useCallback(async (
    saveMode: 'add' | 'edit',
    onSuccess: (result: FJ110) => void,
  ) => {
    const { master, details: recalcDetails } = recalcAll(formData, details);
    // Temporarily update state for save
    // The save handler reads formData + details from closure
    // So we need to directly pass calculated values
    const req = {
      master: master as FJ110,
      details: recalcDetails.map((d, i) => ({ ...d, fj111_02: i + 1 })) as FJ111[],
    };
    // Use the j201Api directly instead of handleSave to include recalculated data
    const { j201Api } = await import('../../services/j201Api');
    const { message } = await import('antd');
    try {
      let result: FJ110;
      if (saveMode === 'add') {
        result = await j201Api.create(req);
        message.success(`新增成功: ${result.fj110_01}`);
      } else {
        result = await j201Api.update(master.fj110_01 as string, req);
        message.success('修改成功');
      }
      onSuccess(result);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { errors?: Array<{ message: string }> } } };
      if (axiosErr.response?.status === 422 && axiosErr.response.data?.errors) {
        const msgs = axiosErr.response.data.errors.map(e => e.message).join('\n');
        message.error({ content: msgs, duration: 5 });
      } else {
        const errMsg = err instanceof Error ? err.message : '儲存失敗';
        message.error(errMsg);
      }
    }
  }, [formData, details, recalcAll]);

  // ---- Action handlers ----
  const handleAddAction = useCallback(async () => {
    if (isEditing && mode === 'add') {
      await doRecalcAndSave('add', async (result: FJ110) => {
        const ids = await loadList();
        const newIdx = ids.indexOf(result.fj110_01);
        setCurrentIdx(newIdx >= 0 ? newIdx : 0);
        applyResult(result);
        resolveAllNames(result);
        setMode('browse');
      });
    } else {
      setMode('add');
      resetForAdd();
      clearNames();
      setActiveTab('1');
    }
  }, [isEditing, mode, doRecalcAndSave, loadList, setCurrentIdx, applyResult, resolveAllNames, setMode, resetForAdd, clearNames]);

  const handleModifyAction = useCallback(async () => {
    if (isEditing && mode === 'edit') {
      await doRecalcAndSave('edit', async (result: FJ110) => {
        applyResult(result);
        resolveAllNames(result);
        setMode('browse');
      });
    } else {
      setMode('edit');
    }
  }, [isEditing, mode, doRecalcAndSave, applyResult, resolveAllNames, setMode]);

  const handleDeleteAction = useCallback(() => {
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除申請單 ${record?.fj110_01} 嗎？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        await handleDelete(async () => {
          const ids = await loadList();
          if (ids.length > 0) {
            setCurrentIdx(0);
            await loadRecordWithNames(ids[0]);
          } else {
            resetForAdd();
            clearNames();
          }
          setMode('browse');
        });
      },
    });
  }, [record, handleDelete, loadList, setCurrentIdx, loadRecordWithNames, resetForAdd, clearNames, setMode]);

  const handleExit = useCallback(() => {
    if (isEditing) {
      revertToRecord();
      if (record) resolveAllNames(record);
      setMode('browse');
    } else {
      navigate('/erp');
    }
  }, [isEditing, revertToRecord, record, resolveAllNames, setMode, navigate]);

  // ---- Render ----
  const badge = getApprovalBadge(record?.fj110_80);

  return (
    <div className="j201-form">
      <div className="page-wrapper">
        <div className="page-title-bar">
          <div className="page-title-left">
            <div className="breadcrumb-inline">
              <a href="/erp">ERP</a>
              <span>/</span>
              <span className="current">樣品申請單</span>
            </div>
            <span className="title-separator">|</span>
            <h1>樣品申請單 (pwj201f1)</h1>
            {record && <span className={`badge ${badge.cls}`}>{badge.text}</span>}
          </div>
          <div className="page-title-right">
            <span style={{ fontSize: 12, color: '#999' }}>
              {record?.fj110_01 || '(新增)'}
            </span>
            <span className={`badge ${isEditing ? 'badge-warning' : 'badge-default'}`}>
              {mode === 'add' ? '新增' : mode === 'edit' ? '修改' : '瀏覽'}
            </span>
          </div>
        </div>

        <div className="form-content">
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: '1',
                label: '基本資料',
                children: (
                  <J201FormFields
                    formData={formData}
                    names={names}
                    isEditing={isEditing}
                    mode={mode}
                    getField={getField}
                    setField={setField}
                    resolveSingleName={resolveSingleName}
                  />
                ),
              },
              {
                key: '2',
                label: `明細 (${details.length})`,
                children: (
                  <J201DetailTable
                    details={details}
                    isEditing={isEditing}
                    isCharged={!!formData.fj110_16}
                    onAdd={addDetailRow}
                    onDelete={deleteDetailRow}
                    onDetailChange={handleDetailChange}
                  />
                ),
              },
            ]}
          />
        </div>

        <SharedActionBar
          mode={mode}
          isEditing={isEditing}
          hasRecord={!!record}
          currentIdx={currentIdx}
          recordCount={allIds.length}
          onNavFirst={() => navTo(0, isEditing)}
          onNavPrev={() => navTo(currentIdx - 1, isEditing)}
          onNavNext={() => navTo(currentIdx + 1, isEditing)}
          onNavLast={() => navTo(allIds.length - 1, isEditing)}
          onAdd={handleAddAction}
          onModify={handleModifyAction}
          onDelete={handleDeleteAction}
          approval={{
            statusCode: record?.fj110_80 || '',
            isApproved: record?.fj110_80 === 'P',
            isPending: ['D', 'S'].includes(record?.fj110_80 || ''), // 統一碼 D + 舊碼 S 過渡容忍
            isLocked: ['P', 'D', 'S'].includes(record?.fj110_80 || ''),
          }}
          onSubmit={handleSubmit}
          onApprove={handleApprove}
          onReject={handleReject}
          onWriteoff={handleWriteoff}
          onHistory={handleViewHistory}
          onExit={handleExit}
          canCreate={permissions.canCreate}
          canEdit={permissions.canEdit}
          canDelete={permissions.canDelete}
          canApprove={permissions.canApprove}
        />
        <BatchAcceptanceBridge
          manifest={pwj201f1AcceptanceManifest}
          records={record ? [record] : []}
          selectedKeys={record?.fj110_01 ? [record.fj110_01] : []}
          keyOf={(item) => item.fj110_01}
          queryExact={(key) => j201Api.get(key)}
          lastApiResponse={record}
          loading={loading}
          onAutoFill={() => allIds[0] && navTo(0, false)}
          autoFillDisabled={allIds.length === 0}
          screenDraft={{ master: formData, details }}
        />
      </div>

      <ApprovalHistoryModal
        visible={approvalHistoryVisible}
        requestNo={record?.fj110_01 || ''}
        onClose={() => setApprovalHistoryVisible(false)}
      />
    </div>
  );
};

export default J201Form;
